import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Qué va a pasar, con el objeto y la consecuencia: "Se devolverán 1.000 puntos a Ana Gómez". */
  description: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  /** Campos extra (p. ej. un motivo opcional). */
  children?: React.ReactNode;
  onConfirm: () => Promise<unknown> | void;
  testid?: string;
};

// Confirmación para acciones que no se pueden deshacer o que afectan a muchos clientes.
// Mantiene el diálogo abierto si onConfirm falla, para que la persona pueda reintentar.
export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel, danger, children, onConfirm, testid }: Props) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // el error ya se mostró en un aviso; el diálogo queda abierto
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o: boolean) => !busy && onOpenChange(o)}>
      <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{title}</DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm text-slate-700 space-y-2">{description}</div>
          </DialogDescription>
        </DialogHeader>
        {children}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-slate-200 text-slate-700 rounded-full">
            Cancelar
          </Button>
          <Button
            data-testid={testid ? `${testid}-confirm` : "confirm-dialog-confirm"}
            onClick={run}
            disabled={busy}
            className={`${danger ? "bg-red-700 hover:bg-red-800" : "gold-gradient-bg"} text-white font-semibold rounded-full`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Procesando" /> : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
